/**
 * ─── Pawnshop Dashboard + Tickets ──────────────────────────
 * Full pawnshop operations: customers, items, pawn tickets,
 * payments, auctions. Uses PawnshopService for all computations.
 * Double-entry ledger entries displayed per transaction.
 */

import { useEffect, useState, useCallback } from 'react'
import { Link } from '@tanstack/react-router'
import { Card, CardHeader, Badge, Button, Input, Modal } from '@repo/ui-core'
import {
  pawnCustomerRepo, pawnItemRepo, pawnTicketRepo, pawnPaymentRepo, pawnAuctionRepo,
} from '../../lib/db'
import {
  PawnshopService, DEFAULT_PAWNSHOP_CONFIG,
  TICKET_STATUS_LABELS, ITEM_CATEGORY_LABELS, ITEM_CONDITION_LABELS,
  ITEM_STATUS_LABELS, CUSTOMER_STATUS_LABELS, PAYMENT_TYPE_LABELS,
  PAYMENT_METHOD_LABELS, AUCTION_STATUS_LABELS,
} from '@repo/entity-pawnshop'
import type { LedgerEntry } from '@repo/entity-pawnshop'
import {
  Gem, Users, FileText, DollarSign, Gavel, TrendingUp,
  AlertTriangle, Clock, ArrowRight, Plus, Calculator,
} from 'lucide-react'

const pawnService = new PawnshopService(DEFAULT_PAWNSHOP_CONFIG)

// ═══════════════════════════════════════════════════════════════
// Dashboard
// ═══════════════════════════════════════════════════════════════

export function PawnshopDashboardPage() {
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<any>({
    activeTickets: 0, totalPrincipal: 0, interestEarned: 0,
    itemsInVault: 0, expiringSoon: 0, overdue: 0, redemptionRate: 0, customers: 0,
  })
  const [tickets, setTickets] = useState<any[]>([])
  const [items, setItems] = useState<any[]>([])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [t, i, c, p] = await Promise.all([
        pawnTicketRepo.findMany({ page: 1, pageSize: 500, sort: [{ field: 'createdAt', direction: 'desc' }] }),
        pawnItemRepo.findMany({ page: 1, pageSize: 500 }),
        pawnCustomerRepo.findMany({ page: 1, pageSize: 500 }),
        pawnPaymentRepo.findMany({ page: 1, pageSize: 500 }),
      ])
      const ticketsList: any[] = 'items' in t ? t.items : []
      const itemsList: any[] = 'items' in i ? i.items : []
      const customersList: any[] = 'items' in c ? c.items : []
      const paymentsList: any[] = 'items' in p ? p.items : []

      setTickets(ticketsList)
      setItems(itemsList)

      const dashboardStats = pawnService.computeDashboardStats(ticketsList, itemsList, paymentsList)
      setStats({ ...dashboardStats, customers: customersList.length })
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  const now = Date.now()
  const activeTickets = tickets.filter((t: any) => t.status === 'active' || t.status === 'renewed')
  const overdueTickets = activeTickets.filter((t: any) => t.maturityDate < now)
  const expiringTickets = activeTickets.filter((t: any) => t.maturityDate > now && t.maturityDate < now + 7 * 86400000)

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Pawnshop Management</h2>
          <p className="mt-1 text-sm text-gray-500">Double-entry ledger • DDD Aggregate Root (PawnTicket) • Metadata-driven rates</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ['Active Tickets', stats.activeTickets, 'green', FileText],
          ['Principal Outstanding', `₱${(stats.totalPrincipal ?? 0).toLocaleString()}`, 'blue', DollarSign],
          ['Interest Earned', `₱${(stats.interestEarned ?? 0).toLocaleString()}`, 'amber', TrendingUp],
          ['Items in Vault', stats.itemsInVault, 'purple', Gem],
          ['Customers', stats.customers, 'indigo', Users],
          ['Expiring Soon', stats.expiringSoon, 'yellow', Clock],
          ['Overdue', stats.overdue, 'red', AlertTriangle],
          ['Redemption Rate', `${stats.redemptionRate ?? 0}%`, 'blue', TrendingUp],
        ].map(([label, value, color, Icon]: any) => (
          <div key={label} className={`rounded-xl border border-${color}-200 bg-${color}-50 p-4`}>
            <div className="flex items-center gap-2">
              {Icon && <Icon className={`h-4 w-4 text-${color}-600`} />}
              <p className={`text-xs text-${color}-600`}>{label}</p>
            </div>
            <p className={`mt-1 text-xl font-bold text-${color}-700`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Active Tickets */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="Active Tickets" description={`${activeTickets.length} active`}
            action={<Link to="/pawnshop/tickets" className="text-xs text-blue-600 hover:underline flex items-center gap-1">View All <ArrowRight className="h-3 w-3" /></Link>} />
          <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
            {activeTickets.slice(0, 10).map((t: any) => {
              const redemption = pawnService.computeRedemptionAmount(t)
              const isOverdue = t.maturityDate < now
              const isExpiring = t.maturityDate > now && t.maturityDate < now + 7 * 86400000
              return (
                <div key={t.id} className="flex items-center justify-between p-4 hover:bg-gray-50">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t.ticketNumber}</p>
                    <p className="text-xs text-gray-500">{t.customerName ?? t.customerId?.slice(0, 8)} • ₱{(t.principalAmount ?? 0).toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-bold ${isOverdue ? 'text-red-600' : 'text-green-700'}`}>₱{redemption.toLocaleString()}</p>
                    <p className={`text-xs ${isOverdue ? 'text-red-500' : isExpiring ? 'text-yellow-600' : 'text-gray-400'}`}>
                      {isOverdue ? 'OVERDUE' : isExpiring ? 'Expiring' : 'Active'}
                    </p>
                  </div>
                </div>
              )
            })}
            {activeTickets.length === 0 && <p className="p-4 text-sm text-gray-400">No active tickets.</p>}
          </div>
        </Card>

        {/* Overdue Alerts */}
        <Card>
          <CardHeader title="Overdue & Expiring" description={`${overdueTickets.length} overdue, ${expiringTickets.length} expiring soon`} />
          <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
            {[...overdueTickets, ...expiringTickets].slice(0, 10).map((t: any) => {
              const redemption = pawnService.computeRedemptionAmount(t)
              const daysOverdue = Math.ceil((now - t.maturityDate) / 86400000)
              return (
                <div key={t.id} className="flex items-center justify-between p-4 hover:bg-gray-50">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t.ticketNumber}</p>
                    <p className="text-xs text-gray-500">{t.customerName}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-red-600">₱{redemption.toLocaleString()}</p>
                    {daysOverdue > 0 && <Badge color="red">{daysOverdue}d overdue</Badge>}
                    {daysOverdue <= 0 && <Badge color="yellow">Expiring</Badge>}
                  </div>
                </div>
              )
            })}
            {overdueTickets.length === 0 && expiringTickets.length === 0 && <p className="p-4 text-sm text-gray-400">All tickets are in good standing.</p>}
          </div>
        </Card>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Pawn Ticket Page (Create + List + Redeem/Renew)
// ═══════════════════════════════════════════════════════════════

export function PawnshopTicketPage() {
  const [tickets, setTickets] = useState<any[]>([])
  const [customers, setCustomers] = useState<any[]>([])
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [saving, setSaving] = useState(false)
  const [ledger, setLedger] = useState<LedgerEntry[]>([])
  const [form, setForm] = useState({
    customerId: '', itemIds: [] as string[], principalAmount: 0,
    interestRate: DEFAULT_PAWNSHOP_CONFIG.defaultInterestRate,
    advanceInterestMonths: 1, notes: '',
  })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [t, c, i] = await Promise.all([
        pawnTicketRepo.findMany({ page: 1, pageSize: 500, sort: [{ field: 'createdAt', direction: 'desc' }] }),
        pawnCustomerRepo.findMany({ page: 1, pageSize: 500 }),
        pawnItemRepo.findMany({ page: 1, pageSize: 500 }),
      ])
      setTickets('items' in t ? t.items : [])
      setCustomers('items' in c ? c.items : [])
      setItems('items' in i ? i.items : [])
    } catch (e) { console.error(e) } finally { setLoading(false) }
  }, [])
  useEffect(() => { load() }, [load])

  async function createTicket() {
    setSaving(true)
    try {
      const customer = customers.find((c: any) => c.id === form.customerId)
      const result = pawnService.createTicket({
        customerId: form.customerId,
        customerName: customer?.fullName ?? 'Unknown',
        itemIds: form.itemIds,
        principalAmount: Number(form.principalAmount),
        interestRate: Number(form.interestRate),
        advanceInterestMonths: Number(form.advanceInterestMonths),
        notes: form.notes,
      })

      if (!result.success || !result.data) {
        alert(result.error)
        setSaving(false)
        return
      }

      await pawnTicketRepo.create({
        ...result.data,
        tenantId: 'default', createdBy: 'admin', updatedBy: 'admin',
      } as any)

      // Update items to pawned status
      for (const itemId of form.itemIds) {
        try {
          await pawnItemRepo.update(itemId, {
            status: 'pawned', ticketId: (result.data as any).id ?? undefined,
            pawnDate: Date.now(), maturityDate: result.data.maturityDate,
            updatedBy: 'admin',
          } as any)
        } catch (e) { console.error(e) }
      }

      setLedger(result.ledgerEntries ?? [])
      setShowCreate(false)
      setForm({ customerId: '', itemIds: [], principalAmount: 0,
        interestRate: DEFAULT_PAWNSHOP_CONFIG.defaultInterestRate,
        advanceInterestMonths: 1, notes: '' })
      await load()
    } catch (e) { console.error(e) } finally { setSaving(false) }
  }

  async function handleRedeem(ticket: any) {
    const amount = pawnService.computeRedemptionAmount(ticket)
    const confirmed = confirm(`Redemption amount: ₱${amount.toLocaleString()}\n\nProceed with full redemption?`)
    if (!confirmed) return
    try {
      const payRes = await pawnPaymentRepo.findMany({ page: 1, pageSize: 500 })
      const allPayments: any[] = 'items' in payRes ? payRes.items : []
      const ticketPayments = allPayments.filter((p: any) => p.ticketId === ticket.id)

      const paymentAmount = amount - ticketPayments.reduce((s: number, p: any) => s + p.amount, 0)
      if (paymentAmount > 0) {
        await pawnPaymentRepo.create({
          ticketId: ticket.id, ticketNumber: ticket.ticketNumber,
          customerId: ticket.customerId, customerName: ticket.customerName,
          paymentType: 'redemption', amount: paymentAmount,
          paymentMethod: 'cash', paymentDate: Date.now(),
          remainingBalanceAfter: 0,
          tenantId: 'default', createdBy: 'admin', updatedBy: 'admin',
        } as any)
      }

      const result = pawnService.redeemTicket(ticket, [...ticketPayments, { amount: paymentAmount }] as any[])
      if (!result.success) { alert(result.error); return }

      await pawnTicketRepo.update(ticket.id, { ...result.data, updatedBy: 'admin' } as any)

      // Update items
      for (const itemId of (ticket.itemIds ?? [])) {
        try { await pawnItemRepo.update(itemId, { status: 'redeemed', redeemedDate: Date.now(), updatedBy: 'admin' } as any) }
        catch (e) { console.error(e) }
      }

      setLedger(result.ledgerEntries ?? [])
      await load()
    } catch (e) { console.error(e) }
  }

  async function handleRenew(ticket: any) {
    const result = pawnService.renewTicket(ticket)
    if (!result.success) { alert(result.error); return }

    // Pay outstanding interest
    const outstandingInterest = pawnService.computeAccruedInterest(ticket)
    if (outstandingInterest > 0) {
      await pawnPaymentRepo.create({
        ticketId: ticket.id, ticketNumber: ticket.ticketNumber,
        customerId: ticket.customerId, customerName: ticket.customerName,
        paymentType: 'renewal', amount: outstandingInterest,
        paymentMethod: 'cash', paymentDate: Date.now(),
        remainingBalanceAfter: ticket.principalAmount,
        tenantId: 'default', createdBy: 'admin', updatedBy: 'admin',
      } as any)
    }

    await pawnTicketRepo.update(ticket.id, { ...result.data, updatedBy: 'admin' } as any)
    setLedger(result.ledgerEntries ?? [])
    await load()
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div><h2 className="text-2xl font-bold text-gray-900">Pawn Tickets</h2><p className="text-sm text-gray-500 mt-1">{tickets.length} tickets</p></div>
        <Button onClick={() => setShowCreate(true)} icon={<Plus className="h-4 w-4" />}>New Pawn Ticket</Button>
      </div>

      {/* Ledger Display */}
      {ledger.length > 0 && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4">
          <p className="text-sm font-semibold text-green-800 mb-2">📒 Double-Entry Ledger</p>
          <table className="w-full text-xs">
            <thead><tr className="text-green-700"><th className="text-left">Account</th><th className="text-right">Debit</th><th className="text-right">Credit</th><th className="text-left">Description</th></tr></thead>
            <tbody>{ledger.map((e, i) => (
              <tr key={i} className="border-t border-green-200"><td className="py-1">{e.account}</td><td className="text-right">{e.debit > 0 ? `₱${e.debit.toLocaleString()}` : ''}</td><td className="text-right">{e.credit > 0 ? `₱${e.credit.toLocaleString()}` : ''}</td><td className="text-gray-500">{e.description}</td></tr>
            ))}</tbody>
          </table>
          <button onClick={() => setLedger([])} className="mt-2 text-xs text-green-600 underline">Dismiss</button>
        </div>
      )}

      <Card><CardHeader title="Ticket Registry" />
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50"><tr>{['Ticket #','Customer','Principal','Rate','Maturity','Redemption','Status',''].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {loading ? <tr><td colSpan={8} className="py-8 text-center text-gray-400">Loading...</td></tr> :
             tickets.length === 0 ? <tr><td colSpan={8} className="py-8 text-center text-gray-400">No tickets.</td></tr> :
             tickets.map((t: any) => {
               const redemption = pawnService.computeRedemptionAmount(t)
               const isOverdue = t.maturityDate < Date.now() && (t.status === 'active' || t.status === 'renewed')
               return (
              <tr key={t.id} className={`hover:bg-gray-50 ${isOverdue ? 'bg-red-50' : ''}`}>
                <td className="px-3 py-2.5 font-mono text-xs font-medium">{t.ticketNumber}</td>
                <td className="px-3 py-2.5 text-xs">{t.customerName ?? t.customerId?.slice(0, 8)}</td>
                <td className="px-3 py-2.5 font-medium text-right">₱{(t.principalAmount ?? 0).toLocaleString()}</td>
                <td className="px-3 py-2.5 text-xs">{((t.interestRate ?? 0) * 100).toFixed(1)}%</td>
                <td className="px-3 py-2.5 text-xs text-gray-500">{t.maturityDate ? new Date(t.maturityDate).toLocaleDateString() : '—'}</td>
                <td className="px-3 py-2.5 font-bold text-right text-green-700">₱{redemption.toLocaleString()}</td>
                <td className="px-3 py-2.5"><Badge color={t.status === 'active' ? 'green' : t.status === 'redeemed' ? 'blue' : t.status === 'expired' ? 'red' : 'yellow'}>{TICKET_STATUS_LABELS[t.status as keyof typeof TICKET_STATUS_LABELS] ?? t.status}</Badge></td>
                <td className="px-3 py-2.5"><div className="flex gap-1">
                  {(t.status === 'active' || t.status === 'renewed') && (
                    <>
                      <button onClick={() => handleRedeem(t)} className="text-xs text-green-600 hover:underline">Redeem</button>
                      <button onClick={() => handleRenew(t)} className="text-xs text-blue-600 hover:underline">Renew</button>
                    </>
                  )}
                </div></td>
              </tr>
             )})}
          </tbody>
        </table></div>
      </Card>

      {/* Create Ticket Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Pawn Ticket" size="lg"
        footer={<div className="flex gap-2"><Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={createTicket} loading={saving} icon={<FileText className="h-4 w-4" />}>Create Ticket</Button></div>}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
            <select value={form.customerId} onChange={e => setForm({ ...form, customerId: e.target.value })} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              <option value="">Select...</option>{customers.map((c: any) => <option key={c.id} value={c.id}>{c.fullName} ({c.phone})</option>)}</select></div>
          <Input label="Principal Amount (₱)" type="number" value={String(form.principalAmount)} onChange={e => setForm({ ...form, principalAmount: Number(e.target.value) })} />
          <Input label="Interest Rate" type="number" value={String(form.interestRate)} onChange={e => setForm({ ...form, interestRate: Number(e.target.value) })} />
          <Input label="Advance Interest (months)" type="number" value={String(form.advanceInterestMonths)} onChange={e => setForm({ ...form, advanceInterestMonths: Number(e.target.value) })} />
          <Input label="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Items (pawned)</label>
            <div className="max-h-32 overflow-y-auto border rounded-lg p-2 space-y-1">
              {items.filter((i: any) => i.status === 'pawned' || i.status === undefined).map((i: any) => (
                <label key={i.id} className="flex items-center gap-2 text-sm p-1 hover:bg-gray-50 rounded">
                  <input type="checkbox" checked={form.itemIds.includes(i.id)} onChange={e => {
                    setForm({ ...form, itemIds: e.target.checked ? [...form.itemIds, i.id] : form.itemIds.filter(x => x !== i.id) })
                  }} className="rounded" />
                  {i.description} ({ITEM_CATEGORY_LABELS[i.category as keyof typeof ITEM_CATEGORY_LABELS] ?? i.category}) — ₱{(i.appraisalValue ?? 0).toLocaleString()}
                </label>
              ))}
              {items.filter((i: any) => i.status === 'pawned' || i.status === undefined).length === 0 && <p className="text-xs text-gray-400 p-2">No available items. Add items first.</p>}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

import { PawnshopCustomerPage } from './pawnshop-customers'
import { PawnshopItemPage } from './pawnshop-customers'
